import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import RecognizeWineModal from 'components/Common/Modals/RecognizeWine'
import {
  ChipsContainer,
  ChipItem,
  Item,
  ItemLabel,
  ChipsListGrid5,
  ToggleChipList,
} from 'components/Pages/Wine/View/Main/Content/ProductSpecs/_styles'
import styled from 'styled-components'
import ChevronDown from 'components/Common/Icons/ChevronDownOld'
import { useCollapse } from 'utils/hooks'
import { useDragToScroll } from 'utils/hooks/useDragToScroll'
import useScreen from 'utils/screen'
import { useProduct } from 'components/Pages/Wine/View/Main/_methods'
import { useCapturedWine } from '../../_methods/context'
import { useRecognizeAdd } from '../useRecognizeAdd'

const VintageItem = styled(ChipItem)`
  padding: 5px 11px;
`

const AddButton = styled.button`
  appearance: none;
  border: 1px solid #e7e7ea;
  border-radius: 5px;
  background: none;
  cursor: pointer;
  height: 28px;
  padding: 5px 11px;
  font: var(--typography-14-16-semibold);
  color: #717284;
  white-space: nowrap;

  &:hover {
    border-color: #88122b;
  }
`

const Vintages = () => {
  const { t } = useTranslation()
  const product = useProduct()
  const { capturedWine, getCapturedWineUrl } = useCapturedWine()
  const capturedWineUrl = (id: string) =>
    getCapturedWineUrl ? getCapturedWineUrl(id) : `/acc/captured-wine/${id}`
  const { dialogOpen, setDialogOpen, handleConfirm, canAdd } = useRecognizeAdd(
    capturedWine.id
  )

  const thisWineVintage = product?.wine?.vintageText

  const vintageList = (capturedWine.vintageList ?? []).map(v => ({
    id: v.id,
    vintageText: v.product?.wine?.vintageText,
  }))

  const { isDesktop } = useScreen()
  const { opened, toggle, contentProps } = useCollapse({
    enabled: isDesktop,
    minHeight: 'var(--min-height)',
  })
  const dragToScrollProps = useDragToScroll()

  const [isShowMoreVisible, setShowMoreVisible] = useState(false)

  return (
    <Item>
      <ItemLabel>{t('wine.view.vintageList.label')}</ItemLabel>
      <ChipsContainer>
        <ChipsListGrid5
          ref={ref => {
            setShowMoreVisible((ref?.scrollHeight ?? 0) > 28 * 2 + 8)
          }}
          {...(isDesktop ? contentProps : dragToScrollProps)}
        >
          <VintageItem
            selected
            to={capturedWineUrl(capturedWine.id)}
            data-testid={`captured-wine-vintage-current`}
          >
            {thisWineVintage ?? t('common.nonVintage')}
          </VintageItem>

          {vintageList
            .filter(v => v.vintageText !== thisWineVintage)
            .map(v => (
              <VintageItem
                key={v.id}
                to={capturedWineUrl(v.id)}
                data-testid={`captured-wine-vintage-item-${v.id}`}
              >
                {v.vintageText ?? t('common.nonVintage')}
              </VintageItem>
            ))}

          {canAdd && (
            <AddButton
              data-testid='captured-wine-vintage-add-button'
              onClick={() => setDialogOpen(true)}
            >
              + {t('wine.view.vintageList.add')}
            </AddButton>
          )}
        </ChipsListGrid5>

        {isShowMoreVisible && (
          <ToggleChipList onClick={toggle}>
            {opened ? t('wine.view.showLess') : t('wine.view.showMore')}
            <ChevronDown opened={opened} color='#3b42a5' />
          </ToggleChipList>
        )}
      </ChipsContainer>

      {dialogOpen && (
        <RecognizeWineModal
          onClose={() => setDialogOpen(false)}
          onConfirm={handleConfirm}
        />
      )}
    </Item>
  )
}

export default Vintages
